/**
 * RCA Service — Root Cause Analysis (PRD_RCA_v1)
 * Colecție Firestore: `rca`. Anchor obligatoriu: deviationId.
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  runTransaction,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import auditService from './auditService';

const COLLECTION = 'rca';
const SEQ_COLLECTION = 'org_sequences';

function omitUndefined(obj) {
  if (obj == null || typeof obj !== 'object') return obj;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v;
  }
  return out;
}

function normalizeRootCauses(causes) {
  if (!Array.isArray(causes) || causes.length === 0) return [];
  return causes.map((c) => ({
    causeId: c.causeId || (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `cause-${Date.now()}-${Math.random()}`),
    causeType: c.causeType || 'human',
    description: (c.description || '').trim(),
    isContributingFactor: !!c.isContributingFactor
  }));
}

export class RCAService {
  constructor() {
    this.collectionRef = collection(db, COLLECTION);
  }

  async _generateRcaCode(organizationId) {
    const seqRef = doc(db, SEQ_COLLECTION, organizationId);
    const seq = await runTransaction(db, async (tx) => {
      const snap = await tx.get(seqRef);
      const current = snap.exists() ? snap.data().rca_last_seq || 0 : 0;
      const next = current + 1;
      if (snap.exists()) {
        tx.update(seqRef, { rca_last_seq: next, updated_at: serverTimestamp() });
      } else {
        tx.set(seqRef, { rca_last_seq: next, updated_at: serverTimestamp() });
      }
      return next;
    });
    const year = new Date().getFullYear();
    return `RCA-${year}-${String(seq).padStart(3, '0')}`;
  }

  /**
   * @param {object} data
   * @param {string} userId
   * @returns {Promise<string>} rcaId
   */
  async createRCA(data, userId) {
    if (!data.deviationId) throw new Error('[RCAService] deviationId este obligatoriu');
    if (!data.organizationId) throw new Error('[RCAService] organizationId este obligatoriu');
    const normalized = normalizeRootCauses(data.rootCauses);
    if (normalized.length === 0) {
      throw new Error('[RCAService] Minimum o cauză este obligatorie');
    }
    for (const c of normalized) {
      if (!c.description) {
        throw new Error('[RCAService] Fiecare cauză trebuie să aibă descriere');
      }
    }

    const rca_code = await this._generateRcaCode(data.organizationId);
    const now = Timestamp.now();

    const payload = omitUndefined({
      rca_code,
      organizationId: data.organizationId,
      deviationId: data.deviationId,
      incidentId: data.incidentId || null,
      status: 'open',
      assignedTo: data.assignedTo || null,
      assignedToName: data.assignedToName || '',
      rootCauses: normalized,
      contributingFactorsText: data.contributingFactorsText || '',
      findings: data.findings || '',
      recommendations: data.recommendations || '',
      protocolId: data.protocolId || null,
      protocolName: data.protocolName || null,
      riskId: data.riskId || null,
      riskName: data.riskName || null,
      capaIds: [],
      evidenceRefs: [],
      createdAt: now,
      createdBy: userId,
      updatedAt: now,
      updatedBy: userId,
      resolvedAt: null,
      resolvedBy: null
    });

    const ref = await addDoc(this.collectionRef, payload);

    import('./deviationService')
      .then(({ deviationService }) => {
        deviationService.linkRca(data.deviationId, ref.id, userId).catch((e) =>
          console.warn('[RCAService] linkRca on deviation failed (non-blocking):', e)
        );
      })
      .catch((e) => console.warn('[RCAService] deviationService import failed:', e));

    if (data.incidentId) {
      import('./incidentService')
        .then(({ incidentService }) => {
          incidentService.linkRCA(data.incidentId, ref.id, userId).catch((e) =>
            console.warn('[RCAService] linkRCA on incident failed (non-blocking):', e)
          );
        })
        .catch((e) => console.warn('[RCAService] incidentService import failed:', e));
    }

    try {
      auditService.logCreate(COLLECTION, ref.id, rca_code, {
        deviationId: data.deviationId,
        organizationId: data.organizationId
      });
    } catch (e) {
      console.warn('[RCAService] audit log failed:', e);
    }

    return ref.id;
  }

  async getRCA(rcaId) {
    const ref = doc(this.collectionRef, rcaId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  }

  async getRCAsByOrganization(organizationId) {
    const q = query(
      this.collectionRef,
      where('organizationId', '==', organizationId),
      orderBy('createdAt', 'desc')
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  async getRCAsByDeviation(deviationId) {
    const q = query(
      this.collectionRef,
      where('deviationId', '==', deviationId),
      orderBy('createdAt', 'desc')
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  async getRCAsByIncident(incidentId) {
    const q = query(
      this.collectionRef,
      where('incidentId', '==', incidentId),
      orderBy('createdAt', 'desc')
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  async updateRCA(rcaId, updates, userId) {
    const ref = doc(this.collectionRef, rcaId);
    const patch = omitUndefined({
      ...updates,
      updatedAt: Timestamp.now(),
      updatedBy: userId
    });
    await updateDoc(ref, patch);
    try {
      auditService.logUpdate(COLLECTION, rcaId, rcaId, { changedFields: Object.keys(updates) });
    } catch (e) {
      console.warn('[RCAService] audit log update failed:', e);
    }
  }

  async updateStatus(rcaId, newStatus, userId) {
    const validStatuses = ['open', 'under_investigation', 'resolved', 'closed'];
    if (!validStatuses.includes(newStatus)) {
      throw new Error(`[RCAService] status invalid: ${newStatus}`);
    }
    await this.updateRCA(rcaId, { status: newStatus }, userId);
  }

  async addRootCause(rcaId, cause, userId) {
    const ref = doc(this.collectionRef, rcaId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('[RCAService] RCA not found');
    const existing = snap.data().rootCauses || [];
    const normalized = normalizeRootCauses([cause])[0];
    await updateDoc(ref, {
      rootCauses: [...existing, normalized],
      updatedAt: Timestamp.now(),
      updatedBy: userId
    });
  }

  async removeRootCause(rcaId, causeId, userId) {
    const ref = doc(this.collectionRef, rcaId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('[RCAService] RCA not found');
    const filtered = (snap.data().rootCauses || []).filter((c) => c.causeId !== causeId);
    await updateDoc(ref, {
      rootCauses: filtered,
      updatedAt: Timestamp.now(),
      updatedBy: userId
    });
  }

  async linkCAPA(rcaId, capaId, userId) {
    const ref = doc(this.collectionRef, rcaId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('[RCAService] RCA not found');
    const existing = snap.data().capaIds || [];
    if (existing.includes(capaId)) return;
    await updateDoc(ref, {
      capaIds: [...existing, capaId],
      updatedAt: Timestamp.now(),
      updatedBy: userId
    });
  }

  async addEvidence(rcaId, evidenceRef, userId) {
    const ref = doc(this.collectionRef, rcaId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('[RCAService] RCA not found');
    const existing = snap.data().evidenceRefs || [];
    await updateDoc(ref, {
      evidenceRefs: [
        ...existing,
        {
          ...evidenceRef,
          uploadedAt: Timestamp.now(),
          uploadedBy: userId
        }
      ],
      updatedAt: Timestamp.now(),
      updatedBy: userId
    });
  }

  async closeRCA(rcaId, userId) {
    const ref = doc(this.collectionRef, rcaId);
    const now = Timestamp.now();
    await updateDoc(ref, {
      status: 'closed',
      resolvedAt: now,
      resolvedBy: userId,
      updatedAt: now,
      updatedBy: userId
    });
    try {
      auditService.logUpdate(COLLECTION, rcaId, rcaId, { action: 'close' });
    } catch (e) {
      console.warn('[RCAService] audit log close failed:', e);
    }
  }
}

export const rcaService = new RCAService();
